import {
    ApiMethod,
    MessageType,
    MIN_PROTOCOL_VERSION,
    PROTOCOL_VERSION,
    RealtimeFeedKind,
    createMessage,
    isBridgeMessage,
    isSupportedProtocolVersion,
} from './protocol.js';

const SUPPORTED_METHODS = new Set(Object.values(ApiMethod));

/**
 * Normalize and validate a relative API path against an allowlist of prefixes.
 * Absolute http(s) URLs are rejected unless allowAbsoluteUrls is true.
 *
 * @param {string} url
 * @param {string[]} allowedApiPrefixes
 * @param {{ allowAbsoluteUrls?: boolean }} [options]
 * @returns {{ ok: true, url: string } | { ok: false, reason: string }}
 */
export function validateApiUrl(url, allowedApiPrefixes, options = {}) {
    if (!url || typeof url !== 'string')
        return { ok: false, reason: 'URL is required' };

    const trimmed = url.trim();
    const isAbsolute = /^https?:\/\//i.test(trimmed) || trimmed.startsWith('//');

    if (isAbsolute && !options.allowAbsoluteUrls)
        return { ok: false, reason: 'Absolute URLs are not allowed' };

    if (isAbsolute)
        return { ok: true, url: trimmed };

    const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    const prefixes = Array.isArray(allowedApiPrefixes) ? allowedApiPrefixes : [];

    if (prefixes.length === 0)
        return { ok: false, reason: 'No AllowedApiPrefixes configured' };

    const allowed = prefixes.some((prefix) => {
        if (prefix === undefined || prefix === null)
            return false;
        const normalized = String(prefix).replace(/^\//, '');
        if (!normalized)
            return true;
        if (path === normalized)
            return true;
        if (normalized.endsWith('/'))
            return path.startsWith(normalized);
        return path.startsWith(`${normalized}/`);
    });

    if (!allowed)
        return { ok: false, reason: `URL is not in AllowedApiPrefixes: ${path}` };

    return { ok: true, url: path };
}

/**
 * Resolve whether a message event origin is permitted for this sandboxed app.
 *
 * @param {string} eventOrigin
 * @param {string | string[] | (() => string | string[] | null | undefined)} allowedOrigin
 * @returns {boolean}
 */
export function isAllowedOrigin(eventOrigin, allowedOrigin) {
    if (!eventOrigin)
        return false;

    const resolved = typeof allowedOrigin === 'function' ? allowedOrigin() : allowedOrigin;
    if (!resolved)
        return false;

    const list = Array.isArray(resolved) ? resolved : [resolved];
    return list.some((origin) => origin && (origin === '*' || origin === eventOrigin));
}

/**
 * @param {boolean} allowAll
 * @param {Array<{ kind?: string, name?: string }> | null | undefined} allowedFeeds
 * @param {string} kind
 * @param {string} name
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function validateRealtimeFeed(allowAll, allowedFeeds, kind, name) {
    if (allowAll)
        return { ok: true };

    const feeds = Array.isArray(allowedFeeds) ? allowedFeeds : [];
    if (feeds.length === 0)
        return { ok: false, reason: 'No AllowedRealtimeFeeds configured' };

    const allowed = feeds.some((feed) =>
        feed
        && String(feed.kind) === kind
        && String(feed.name) === name
    );

    if (!allowed)
        return { ok: false, reason: `Realtime feed is not allowed: ${kind}/${name}` };

    return { ok: true };
}

/**
 * Validate event-name allowlists for serverEvents / bus.
 * Empty `requestedNames` means "all events" and requires allowAll.
 *
 * @param {boolean} allowAll
 * @param {string[] | null | undefined} allowedNames
 * @param {string[]} requestedNames
 * @param {string} allowlistLabel e.g. 'AllowedServerEventNames'
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function validateEventNameAllowlist(allowAll, allowedNames, requestedNames, allowlistLabel) {
    if (allowAll)
        return { ok: true };

    const allowed = Array.isArray(allowedNames)
        ? allowedNames.map((n) => String(n)).filter(Boolean)
        : [];
    if (allowed.length === 0)
        return { ok: false, reason: `No ${allowlistLabel} configured` };

    const requested = Array.isArray(requestedNames) ? requestedNames : [];
    if (requested.length === 0)
        return { ok: false, reason: `Subscribing to all event names requires allow-all; list names or set allow-all` };

    for (const name of requested) {
        if (!allowed.includes(name))
            return { ok: false, reason: `Event name is not allowed: ${name}` };
    }

    return { ok: true };
}

/**
 * Top-level primitives stay strings; plain objects/arrays are JSON-cloned (nested types preserved).
 * @param {unknown} value
 * @returns {unknown}
 */
function normalizeParamValue(value) {
    if (value === undefined || value === null)
        return '';
    if (typeof value === 'object') {
        try {
            return JSON.parse(JSON.stringify(value));
        }
        catch {
            return String(value);
        }
    }
    return String(value);
}

/**
 * @param {unknown} raw
 * @returns {Record<string, unknown>}
 */
function normalizeParams(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    const out = {};
    for (const key of Object.keys(raw))
        out[key] = normalizeParamValue(raw[key]);
    return out;
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
function normalizeRoute(raw) {
    if (raw === undefined || raw === null)
        return '';
    return String(raw);
}

/**
 * @param {unknown} raw
 * @returns {{ colors: Record<string, string>, name: string } | null}
 */
function normalizeThemeSnapshot(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return null;

    const name = raw.name == null ? '' : String(raw.name);
    const source = raw.colors && typeof raw.colors === 'object' && !Array.isArray(raw.colors)
        ? raw.colors
        : raw;

    const colors = {};
    for (const key of Object.keys(source)) {
        if (key === 'name' || key === 'colors')
            continue;
        const value = source[key];
        if (typeof value === 'string' && value.trim())
            colors[key] = value.trim();
    }

    if (Object.keys(colors).length === 0)
        return null;

    return { colors, name };
}

/**
 * Strip Vue proxies / non-cloneable values before postMessage.
 * @param {unknown} value
 * @returns {unknown}
 */
export function toPlainJson(value) {
    if (value === undefined)
        return null;
    try {
        return JSON.parse(JSON.stringify(value));
    }
    catch {
        return null;
    }
}

/**
 * Normalize a portal ChannelEvent into the guest serverEvents payload.
 * @param {unknown} raw
 * @returns {{ sessionId: string, name: string, time: string, content: unknown, id: string | null } | null}
 */
export function normalizeServerEvent(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return null;

    let content = Object.prototype.hasOwnProperty.call(raw, 'Content')
        ? raw.Content
        : (Object.prototype.hasOwnProperty.call(raw, 'content') ? raw.content : null);

    if (typeof content === 'string') {
        const trimmed = content.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                content = JSON.parse(trimmed);
            }
            catch {
                // Keep original string when not valid JSON.
            }
        }
    }

    const idRaw = Object.prototype.hasOwnProperty.call(raw, 'EventId')
        ? raw.EventId
        : (Object.prototype.hasOwnProperty.call(raw, 'id') ? raw.id : null);

    return {
        sessionId: String(raw.SessionId ?? raw.sessionId ?? ''),
        name: String(raw.EventName ?? raw.name ?? ''),
        time: String(raw.EventTime ?? raw.time ?? ''),
        content: toPlainJson(content),
        id: idRaw === undefined || idRaw === null ? null : String(idRaw),
    };
}

/**
 * Normalize RTEventHandler view payload for the guest contract.
 * @param {'widget' | 'list'} kind
 * @param {unknown} raw
 * @returns {unknown}
 */
export function normalizeRealtimeSnapshot(kind, raw) {
    if (raw == null)
        return kind === RealtimeFeedKind.List ? { total: 0, rows: [] } : null;

    if (kind === RealtimeFeedKind.List) {
        const obj = typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
        const total = typeof obj.Total === 'number'
            ? obj.Total
            : (typeof obj.total === 'number' ? obj.total : 0);
        const rows = Array.isArray(obj.Data)
            ? obj.Data
            : (Array.isArray(obj.rows) ? obj.rows : (Array.isArray(obj) ? obj : []));
        return toPlainJson({ total, rows });
    }

    return toPlainJson(raw);
}

function generateSubscriptionId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID)
        return crypto.randomUUID();
    return `rt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Create a host-side message handler that proxies allowlisted API calls through the portal api service.
 *
 * @param {object} options
 * @param {() => string | string[] | null | undefined} options.getAllowedOrigin
 * @param {() => string[]} options.getAllowedApiPrefixes
 * @param {object} options.api Portal api.jsx service (get/post/put/patch/delete_/cachedGet)
 * @param {() => Record<string, unknown> | null | undefined} [options.getContextParams]
 * @param {() => string | null | undefined} [options.getContextRoute]
 * @param {(source: MessageEventSource, origin: string) => void} [options.onReady]
 * @param {(error: unknown, context: object) => void} [options.onError]
 * @param {boolean} [options.allowAbsoluteUrls=false]
 * @param {() => object | null | undefined} [options.getRealtimeSource] RTEventHandler accessor (Views, AddView, …)
 * @param {() => boolean} [options.getAllowAllRealtimeFeeds]
 * @param {() => Array<{ kind: string, name: string }>} [options.getAllowedRealtimeFeeds]
 * @param {(viewKey: string, callback: (value: unknown) => void) => (() => void)} [options.watchRealtimeView]
 * @param {() => MessageEventSource | Window | null | undefined} [options.getGuestWindow]
 *   When set, only messages from this window (typically iframe.contentWindow) are handled.
 *   Required when multiple sandboxed guests share the same origin so hosts do not cross-talk.
 * @param {() => { colors?: Record<string, string>, name?: string } | null | undefined} [options.getTheme]
 *   Snapshot of the active portal / white-label theme for the guest.
 * @param {{ translate?: (text: string, isToolTip?: boolean) => string, hasAccess?: (id: string, name?: string) => boolean }} [options.ui]
 * @param {{ runEventActions?: (name: string, data?: unknown) => void }} [options.events]
 * @param {() => boolean} [options.getAllowAllServerEvents]
 * @param {() => string[]} [options.getAllowedServerEventNames]
 * @param {(opts: { sessionId: string, names: string[], uniqueId: string }, onEvent: (raw: unknown) => void) => (() => void) | Promise<() => void>} [options.subscribeServerEvents]
 *   Portal wires serverEventCoordinator; returns a teardown function.
 * @param {() => boolean} [options.getAllowAllBusEvents]
 * @param {() => string[]} [options.getAllowedBusEvents]
 * @param {(opts: { name: string }, onPayload: (...args: unknown[]) => void) => (() => void) | Promise<() => void>} [options.subscribeBus]
 *   Portal wires EventBus.$on; returns a teardown function.
 * @returns {{
 *   handleMessage: (event: MessageEvent) => void,
 *   pushContextUpdate: (params?: Record<string, unknown>, route?: string) => void,
 *   pushThemeUpdate: (theme?: { colors?: Record<string, string>, name?: string } | null) => void,
 *   dispose: () => void,
 * }}
 */
export function createHostBridgeHandler(options) {
    const {
        getAllowedOrigin,
        getAllowedApiPrefixes,
        api,
        getContextParams,
        getContextRoute,
        getTheme,
        onReady,
        onError,
        allowAbsoluteUrls = false,
        getRealtimeSource,
        getAllowAllRealtimeFeeds,
        getAllowedRealtimeFeeds,
        watchRealtimeView,
        getGuestWindow,
        ui,
        events,
        getAllowAllServerEvents,
        getAllowedServerEventNames,
        subscribeServerEvents,
        getAllowAllBusEvents,
        getAllowedBusEvents,
        subscribeBus,
    } = options;

    let disposed = false;
    /** @type {MessageEventSource | null} */
    let guestSource = null;
    /** @type {string | null} */
    let guestOrigin = null;
    /** Negotiated envelope version for this guest session (guest's Ready version). */
    let sessionProtocolVersion = PROTOCOL_VERSION;

    /** @type {Map<string, {
     *   kind: string,
     *   name: string,
     *   viewKey: string,
     *   serverId: string | null,
     *   filters: object | null,
     *   columns: string[] | null,
     *   sort: object | null,
     *   range: object | null,
     *   revision: number,
     *   unwatch: (() => void) | null,
     * }>} */
    const subscriptions = new Map();

    /** @type {Map<string, { teardown: (() => void) | null }>} */
    const serverEventSubscriptions = new Map();
    /** @type {Map<string, { teardown: (() => void) | null }>} */
    const busSubscriptions = new Map();

    function reply(source, origin, type, requestId, payload, version = sessionProtocolVersion) {
        if (!source || typeof source.postMessage !== 'function')
            return;
        const target = origin && origin !== 'null' ? origin : '*';
        source.postMessage(createMessage({ type, requestId, payload, version }), target);
    }

    /**
     * Advertise host-wired surfaces so newer guests can feature-detect without a protocol bump.
     * @returns {string[]}
     */
    function resolveCapabilities() {
        const caps = ['api', 'context'];
        if (typeof getTheme === 'function')
            caps.push('theme');
        if (typeof getRealtimeSource === 'function')
            caps.push('realtime');
        if (typeof subscribeServerEvents === 'function')
            caps.push('serverEvents');
        if (typeof subscribeBus === 'function')
            caps.push('bus');
        if (ui && typeof ui.translate === 'function')
            caps.push('ui.translate');
        if (ui && typeof ui.hasAccess === 'function')
            caps.push('ui.hasAccess');
        if (events && typeof events.runEventActions === 'function')
            caps.push('events.runEventActions');
        return caps;
    }

    function resolveParams(override) {
        if (override !== undefined)
            return normalizeParams(override);
        if (typeof getContextParams === 'function')
            return normalizeParams(getContextParams());
        return {};
    }

    function resolveRoute(override) {
        if (override !== undefined)
            return normalizeRoute(override);
        if (typeof getContextRoute === 'function')
            return normalizeRoute(getContextRoute());
        return '';
    }

    function resolveTheme(override) {
        if (override !== undefined)
            return normalizeThemeSnapshot(override);
        if (typeof getTheme === 'function')
            return normalizeThemeSnapshot(getTheme());
        return null;
    }

    function resolveRealtimeSource() {
        if (typeof getRealtimeSource !== 'function')
            return null;
        try {
            return getRealtimeSource() || null;
        }
        catch {
            return null;
        }
    }

    function isRealtimeAllowed(kind, name) {
        const allowAll = typeof getAllowAllRealtimeFeeds === 'function'
            ? !!getAllowAllRealtimeFeeds()
            : false;
        const feeds = typeof getAllowedRealtimeFeeds === 'function'
            ? getAllowedRealtimeFeeds()
            : [];
        return validateRealtimeFeed(allowAll, feeds, kind, name);
    }

    function isServerEventAllowed(names) {
        const allowAll = typeof getAllowAllServerEvents === 'function'
            ? !!getAllowAllServerEvents()
            : false;
        const allowed = typeof getAllowedServerEventNames === 'function'
            ? getAllowedServerEventNames()
            : [];
        return validateEventNameAllowlist(allowAll, allowed, names, 'AllowedServerEventNames');
    }

    function isBusEventAllowed(name) {
        const allowAll = typeof getAllowAllBusEvents === 'function'
            ? !!getAllowAllBusEvents()
            : false;
        const allowed = typeof getAllowedBusEvents === 'function'
            ? getAllowedBusEvents()
            : [];
        return validateEventNameAllowlist(allowAll, allowed, [name], 'AllowedBusEvents');
    }

    function resolveGuestWindow() {
        if (typeof getGuestWindow !== 'function')
            return undefined;
        try {
            return getGuestWindow() || null;
        }
        catch {
            return null;
        }
    }

    /** When getGuestWindow is provided, ignore messages from other iframes (same-origin siblings). */
    function isMessageFromBoundGuest(event) {
        if (typeof getGuestWindow !== 'function')
            return true;
        const expected = resolveGuestWindow();
        return !!(expected && event.source === expected);
    }

    function isGuestSourceCurrent() {
        if (!guestSource)
            return false;
        if (typeof getGuestWindow !== 'function')
            return true;
        const expected = resolveGuestWindow();
        return !!(expected && guestSource === expected);
    }

    function pushRealtimeUpdate(subscriptionId, snapshot, revision) {
        if (disposed || !isGuestSourceCurrent())
            return;
        reply(guestSource, guestOrigin, MessageType.RealtimeUpdate, undefined, {
            subscriptionId,
            snapshot,
            revision,
        });
    }

    function pushRealtimeStatus(subscriptionId, status, message) {
        if (disposed || !isGuestSourceCurrent())
            return;
        reply(guestSource, guestOrigin, MessageType.RealtimeStatus, undefined, {
            subscriptionId,
            status,
            message,
        });
    }

    function attachViewWatcher(subscriptionId, kind, viewKey) {
        const sub = subscriptions.get(subscriptionId);
        if (!sub || typeof watchRealtimeView !== 'function')
            return;

        if (sub.unwatch) {
            try { sub.unwatch(); } catch { /* ignore */ }
            sub.unwatch = null;
        }

        sub.unwatch = watchRealtimeView(viewKey, (value) => {
            if (disposed || !subscriptions.has(subscriptionId))
                return;
            if (value && typeof value === 'object' && value.Refreshing)
                return;

            const current = subscriptions.get(subscriptionId);
            if (!current)
                return;

            current.revision += 1;
            const snapshot = normalizeRealtimeSnapshot(kind, value);
            pushRealtimeUpdate(subscriptionId, snapshot, current.revision);
        });
    }

    async function closeSubscription(subscriptionId) {
        const sub = subscriptions.get(subscriptionId);
        if (!sub)
            return;

        subscriptions.delete(subscriptionId);

        if (sub.unwatch) {
            try { sub.unwatch(); } catch { /* ignore */ }
            sub.unwatch = null;
        }

        const source = resolveRealtimeSource();
        if (!source)
            return;

        try {
            if (sub.kind === RealtimeFeedKind.List && typeof source.RemoveNamedListForRealTime === 'function')
                await source.RemoveNamedListForRealTime(sub.name, sub.serverId);
            else if (sub.kind === RealtimeFeedKind.Widget && typeof source.RemoveView === 'function')
                source.RemoveView(sub.name, sub.filters);
        }
        catch (error) {
            if (onError)
                onError(error, { subscriptionId, action: 'close' });
        }
    }

    async function closeServerEventSubscription(subscriptionId) {
        const sub = serverEventSubscriptions.get(subscriptionId);
        if (!sub)
            return;
        serverEventSubscriptions.delete(subscriptionId);
        if (typeof sub.teardown === 'function') {
            try {
                await sub.teardown();
            }
            catch (error) {
                if (onError)
                    onError(error, { subscriptionId, action: 'serverEvents.unsubscribe' });
            }
        }
    }

    async function closeBusSubscription(subscriptionId) {
        const sub = busSubscriptions.get(subscriptionId);
        if (!sub)
            return;
        busSubscriptions.delete(subscriptionId);
        if (typeof sub.teardown === 'function') {
            try {
                await sub.teardown();
            }
            catch (error) {
                if (onError)
                    onError(error, { subscriptionId, action: 'bus.unsubscribe' });
            }
        }
    }

    async function closeAllPushSubscriptions() {
        const serverIds = [...serverEventSubscriptions.keys()];
        for (const id of serverIds)
            await closeServerEventSubscription(id);
        const busIds = [...busSubscriptions.keys()];
        for (const id of busIds)
            await closeBusSubscription(id);
    }

    function pushServerEvent(subscriptionId, event) {
        if (disposed || !isGuestSourceCurrent())
            return;
        if (!serverEventSubscriptions.has(subscriptionId))
            return;
        reply(guestSource, guestOrigin, MessageType.ServerEventsEvent, undefined, {
            subscriptionId,
            event: toPlainJson(event),
        });
    }

    function pushBusEvent(subscriptionId, payload) {
        if (disposed || !isGuestSourceCurrent())
            return;
        if (!busSubscriptions.has(subscriptionId))
            return;
        reply(guestSource, guestOrigin, MessageType.BusEvent, undefined, {
            subscriptionId,
            payload: toPlainJson(payload),
        });
    }

    /**
     * @param {unknown} raw
     * @returns {string[]}
     */
    function normalizeRequestedNames(raw) {
        if (raw === undefined || raw === null || raw === '')
            return [];
        if (Array.isArray(raw))
            return raw.map((n) => String(n).trim()).filter(Boolean);
        if (typeof raw === 'string')
            return raw.split(',').map((n) => n.trim()).filter(Boolean);
        return [String(raw).trim()].filter(Boolean);
    }

    async function openServerEventSubscription(payload) {
        const sessionId = payload?.sessionId;
        if (!sessionId || typeof sessionId !== 'string')
            return { error: { message: 'sessionId is required', code: 'invalid_payload' } };

        const names = normalizeRequestedNames(payload?.names);
        const auth = isServerEventAllowed(names);
        if (!auth.ok)
            return { error: { message: auth.reason, code: 'not_allowed' } };

        if (typeof subscribeServerEvents !== 'function')
            return { error: { message: 'serverEvents not available', code: 'not_supported' } };

        const subscriptionId = generateSubscriptionId();
        const uniqueId = `bridge:serverEvents:${subscriptionId}`;

        try {
            serverEventSubscriptions.set(subscriptionId, { teardown: null });

            const teardown = await subscribeServerEvents(
                { sessionId, names, uniqueId },
                (raw) => {
                    const event = normalizeServerEvent(raw);
                    if (!event)
                        return;
                    // When an allowlist is active and guest asked for specific names,
                    // coordinator already filters; when allow-all with empty names, all pass.
                    pushServerEvent(subscriptionId, event);
                }
            );

            if (!serverEventSubscriptions.has(subscriptionId)) {
                // Unsubscribed during async setup (Ready re-handshake / dispose).
                if (typeof teardown === 'function') {
                    try { await teardown(); } catch { /* ignore */ }
                }
                return { error: { message: 'Subscription cancelled', code: 'cancelled' } };
            }

            serverEventSubscriptions.set(subscriptionId, {
                teardown: typeof teardown === 'function' ? teardown : null,
            });

            return { result: { subscriptionId } };
        }
        catch (error) {
            serverEventSubscriptions.delete(subscriptionId);
            if (onError)
                onError(error, { action: 'serverEvents.subscribe', sessionId });
            return {
                error: {
                    message: String(error?.message || error || 'subscribe_failed'),
                    code: 'subscribe_failed',
                },
            };
        }
    }

    async function openBusSubscription(payload) {
        const name = payload?.name;
        if (!name || typeof name !== 'string')
            return { error: { message: 'name is required', code: 'invalid_payload' } };

        const auth = isBusEventAllowed(name);
        if (!auth.ok)
            return { error: { message: auth.reason, code: 'not_allowed' } };

        if (typeof subscribeBus !== 'function')
            return { error: { message: 'bus not available', code: 'not_supported' } };

        const subscriptionId = generateSubscriptionId();

        try {
            busSubscriptions.set(subscriptionId, { teardown: null });

            const teardown = await subscribeBus(
                { name },
                (...args) => {
                    const payloadValue = args.length <= 1 ? (args[0] ?? null) : args;
                    pushBusEvent(subscriptionId, payloadValue);
                }
            );

            if (!busSubscriptions.has(subscriptionId)) {
                if (typeof teardown === 'function') {
                    try { await teardown(); } catch { /* ignore */ }
                }
                return { error: { message: 'Subscription cancelled', code: 'cancelled' } };
            }

            busSubscriptions.set(subscriptionId, {
                teardown: typeof teardown === 'function' ? teardown : null,
            });

            return { result: { subscriptionId } };
        }
        catch (error) {
            busSubscriptions.delete(subscriptionId);
            if (onError)
                onError(error, { action: 'bus.subscribe', name });
            return {
                error: {
                    message: String(error?.message || error || 'subscribe_failed'),
                    code: 'subscribe_failed',
                },
            };
        }
    }

    async function openRealtimeSubscription(payload) {
        const kind = payload?.kind;
        const name = payload?.name;

        if (kind !== RealtimeFeedKind.Widget && kind !== RealtimeFeedKind.List)
            return { error: { message: 'kind must be "widget" or "list"', code: 'invalid_payload' } };
        if (!name || typeof name !== 'string')
            return { error: { message: 'name is required', code: 'invalid_payload' } };

        const auth = isRealtimeAllowed(kind, name);
        if (!auth.ok)
            return { error: { message: auth.reason, code: 'not_allowed' } };

        const source = resolveRealtimeSource();
        if (!source)
            return { error: { message: 'Realtime source is not available', code: 'not_available' } };

        const filters = payload?.filters && typeof payload.filters === 'object' ? payload.filters : null;
        const columns = Array.isArray(payload?.columns) ? payload.columns.map(String) : null;
        const sort = payload?.sort && typeof payload.sort === 'object' ? payload.sort : null;
        const range = payload?.range && typeof payload.range === 'object' ? payload.range : null;

        const subscriptionId = generateSubscriptionId();

        try {
            if (kind === RealtimeFeedKind.Widget) {
                if (typeof source.AddView !== 'function')
                    return { error: { message: 'Realtime source does not support widget feeds', code: 'not_available' } };

                const serverId = await source.AddView(name, filters);
                const viewKey = serverId ? `${name}~!!~${serverId}` : name;
                const raw = source.Views ? source.Views[viewKey] : null;
                const snapshot = normalizeRealtimeSnapshot(kind, raw);

                subscriptions.set(subscriptionId, {
                    kind,
                    name,
                    viewKey,
                    serverId: serverId != null ? String(serverId) : null,
                    filters,
                    columns: null,
                    sort: null,
                    range: null,
                    revision: 0,
                    unwatch: null,
                });
                attachViewWatcher(subscriptionId, kind, viewKey);

                return { result: { subscriptionId, snapshot } };
            }

            if (typeof source.GetNamedListForRealTime !== 'function')
                return { error: { message: 'Realtime source does not support list feeds', code: 'not_available' } };

            const sortCol = sort?.field || 'StartTime';
            const descending = !!sort?.descending;
            const start = typeof range?.start === 'number' ? range.start : 0;
            const length = typeof range?.length === 'number' ? range.length : 50;
            const serverColumns = columns || [];

            const res = await source.GetNamedListForRealTime(
                name,
                sortCol,
                descending,
                start,
                length,
                serverColumns,
                filters || {},
                undefined
            );

            const serverId = res?.ID != null ? String(res.ID) : null;
            const viewKey = res?.ViewHash || (serverId ? `${name}~!!~${serverId}` : name);
            const snapshot = normalizeRealtimeSnapshot(kind, res?.Response);

            subscriptions.set(subscriptionId, {
                kind,
                name,
                viewKey,
                serverId,
                filters,
                columns: serverColumns,
                sort: { field: sortCol, descending },
                range: { start, length },
                revision: 0,
                unwatch: null,
            });
            attachViewWatcher(subscriptionId, kind, viewKey);

            return { result: { subscriptionId, snapshot } };
        }
        catch (error) {
            if (onError)
                onError(error, { action: 'open', kind, name });
            return {
                error: {
                    message: String(error?.message || error || 'subscribe_failed'),
                    code: 'subscribe_failed',
                },
            };
        }
    }

    async function setRealtimeQuery(payload) {
        const subscriptionId = payload?.subscriptionId;
        if (!subscriptionId || typeof subscriptionId !== 'string')
            return { error: { message: 'subscriptionId is required', code: 'invalid_payload' } };

        const sub = subscriptions.get(subscriptionId);
        if (!sub)
            return { error: { message: 'Unknown subscriptionId', code: 'not_found' } };
        if (sub.kind !== RealtimeFeedKind.List)
            return { error: { message: 'setQuery is only supported for kind "list"', code: 'invalid_payload' } };

        const source = resolveRealtimeSource();
        if (!source || typeof source.GetNamedListForRealTime !== 'function')
            return { error: { message: 'Realtime source is not available', code: 'not_available' } };

        if (payload.filters !== undefined)
            sub.filters = payload.filters && typeof payload.filters === 'object' ? payload.filters : null;
        if (payload.columns !== undefined)
            sub.columns = Array.isArray(payload.columns) ? payload.columns.map(String) : sub.columns;
        if (payload.sort !== undefined && payload.sort && typeof payload.sort === 'object')
            sub.sort = payload.sort;
        if (payload.range !== undefined && payload.range && typeof payload.range === 'object')
            sub.range = payload.range;

        const sortCol = sub.sort?.field || 'StartTime';
        const descending = !!sub.sort?.descending;
        const start = typeof sub.range?.start === 'number' ? sub.range.start : 0;
        const length = typeof sub.range?.length === 'number' ? sub.range.length : 50;

        try {
            const res = await source.GetNamedListForRealTime(
                sub.name,
                sortCol,
                descending,
                start,
                length,
                sub.columns || [],
                sub.filters || {},
                sub.serverId
            );

            if (res?.ID != null)
                sub.serverId = String(res.ID);
            if (res?.ViewHash)
                sub.viewKey = res.ViewHash;

            sub.revision += 1;
            const snapshot = normalizeRealtimeSnapshot(sub.kind, res?.Response);
            attachViewWatcher(subscriptionId, sub.kind, sub.viewKey);

            return { result: { snapshot } };
        }
        catch (error) {
            if (onError)
                onError(error, { action: 'setQuery', subscriptionId });
            return {
                error: {
                    message: String(error?.message || error || 'query_failed'),
                    code: 'query_failed',
                },
            };
        }
    }

    async function executeApi(method, url, data) {
        switch (method) {
            case ApiMethod.GET:
                return api.get(url);
            case ApiMethod.CACHED_GET:
                return api.cachedGet(url);
            case ApiMethod.POST:
                return api.post(url, data);
            case ApiMethod.PUT:
                return api.put(url, data);
            case ApiMethod.PATCH:
                return api.patch(url, data);
            case ApiMethod.DELETE:
                return api.delete_(url);
            default:
                throw new Error(`Unsupported bridge API method: ${method}`);
        }
    }

    async function handleMessage(event) {
        if (disposed || !isBridgeMessage(event.data))
            return;
        if (!isAllowedOrigin(event.origin, getAllowedOrigin))
            return;
        if (!isMessageFromBoundGuest(event))
            return;

        const { type, requestId, payload } = event.data;
        const guestVersion = event.data.version;

        if (type === MessageType.Ready) {
            if (!isSupportedProtocolVersion(guestVersion)) {
                reply(event.source, event.origin, MessageType.ReadyReject, undefined, {
                    code: 'version_mismatch',
                    message: `Unsupported protocol version ${guestVersion}; host supports ${MIN_PROTOCOL_VERSION}–${PROTOCOL_VERSION}`,
                    min: MIN_PROTOCOL_VERSION,
                    max: PROTOCOL_VERSION,
                    guestVersion,
                }, guestVersion);
                return;
            }

            // Drop any subscriptions from a previous guest instance in this iframe.
            const priorIds = [...subscriptions.keys()];
            for (const id of priorIds)
                closeSubscription(id);
            await closeAllPushSubscriptions();

            sessionProtocolVersion = guestVersion;
            guestSource = event.source;
            guestOrigin = event.origin;
            reply(event.source, event.origin, MessageType.ReadyAck, undefined, {
                protocolVersion: PROTOCOL_VERSION,
                negotiatedVersion: sessionProtocolVersion,
                minProtocolVersion: MIN_PROTOCOL_VERSION,
                capabilities: resolveCapabilities(),
                params: resolveParams(),
                route: resolveRoute(),
                theme: resolveTheme(),
            });
            if (onReady)
                onReady(event.source, event.origin);
            return;
        }

        if (!isSupportedProtocolVersion(guestVersion))
            return;
        if (guestSource && guestVersion !== sessionProtocolVersion)
            return;

        if (type === MessageType.RealtimeOpen) {
            const outcome = await openRealtimeSubscription(payload);
            if (outcome.error) {
                reply(event.source, event.origin, MessageType.RealtimeError, requestId, outcome.error);
                return;
            }
            reply(event.source, event.origin, MessageType.RealtimeOpenResult, requestId, outcome.result);
            return;
        }

        if (type === MessageType.RealtimeSetQuery) {
            const outcome = await setRealtimeQuery(payload);
            if (outcome.error) {
                reply(event.source, event.origin, MessageType.RealtimeError, requestId, outcome.error);
                return;
            }
            reply(event.source, event.origin, MessageType.RealtimeSetQueryResult, requestId, outcome.result);
            return;
        }

        if (type === MessageType.RealtimeClose) {
            const subscriptionId = payload?.subscriptionId;
            if (!subscriptionId || typeof subscriptionId !== 'string') {
                reply(event.source, event.origin, MessageType.RealtimeError, requestId, {
                    message: 'subscriptionId is required',
                    code: 'invalid_payload',
                });
                return;
            }
            await closeSubscription(subscriptionId);
            reply(event.source, event.origin, MessageType.RealtimeCloseResult, requestId, {});
            return;
        }

        if (type === MessageType.ServerEventsSubscribe) {
            const outcome = await openServerEventSubscription(payload);
            if (outcome.error) {
                reply(event.source, event.origin, MessageType.ServerEventsError, requestId, outcome.error);
                return;
            }
            reply(event.source, event.origin, MessageType.ServerEventsSubscribeResult, requestId, outcome.result);
            return;
        }

        if (type === MessageType.ServerEventsUnsubscribe) {
            const subscriptionId = payload?.subscriptionId;
            if (!subscriptionId || typeof subscriptionId !== 'string') {
                reply(event.source, event.origin, MessageType.ServerEventsError, requestId, {
                    message: 'subscriptionId is required',
                    code: 'invalid_payload',
                });
                return;
            }
            await closeServerEventSubscription(subscriptionId);
            reply(event.source, event.origin, MessageType.ServerEventsUnsubscribeResult, requestId, {});
            return;
        }

        if (type === MessageType.BusSubscribe) {
            const outcome = await openBusSubscription(payload);
            if (outcome.error) {
                reply(event.source, event.origin, MessageType.BusError, requestId, outcome.error);
                return;
            }
            reply(event.source, event.origin, MessageType.BusSubscribeResult, requestId, outcome.result);
            return;
        }

        if (type === MessageType.BusUnsubscribe) {
            const subscriptionId = payload?.subscriptionId;
            if (!subscriptionId || typeof subscriptionId !== 'string') {
                reply(event.source, event.origin, MessageType.BusError, requestId, {
                    message: 'subscriptionId is required',
                    code: 'invalid_payload',
                });
                return;
            }
            await closeBusSubscription(subscriptionId);
            reply(event.source, event.origin, MessageType.BusUnsubscribeResult, requestId, {});
            return;
        }

        if (type === MessageType.UiRequest) {
            const method = payload?.method;

            if (method === 'translate') {
                if (typeof ui?.translate !== 'function') {
                    reply(event.source, event.origin, MessageType.UiError, requestId, {
                        message: 'translate not available',
                        code: 'not_supported',
                    });
                    return;
                }
                try {
                    const text = payload?.args?.text === undefined || payload?.args?.text === null
                        ? ''
                        : String(payload.args.text);
                    const isToolTip = !!payload?.args?.isToolTip;
                    const result = ui.translate(text, isToolTip);
                    reply(event.source, event.origin, MessageType.UiResponse, requestId, { result });
                }
                catch (error) {
                    if (onError)
                        onError(error, { requestId, payload, action: 'ui.translate' });
                    reply(event.source, event.origin, MessageType.UiError, requestId, {
                        message: String(error?.message || error),
                        code: 'ui_error',
                    });
                }
                return;
            }

            if (method === 'hasAccess') {
                if (typeof ui?.hasAccess !== 'function') {
                    reply(event.source, event.origin, MessageType.UiError, requestId, {
                        message: 'hasAccess not available',
                        code: 'not_supported',
                    });
                    return;
                }
                try {
                    const id = payload?.args?.id === undefined || payload?.args?.id === null
                        ? ''
                        : String(payload.args.id);
                    const name = payload?.args?.name === undefined || payload?.args?.name === null
                        ? undefined
                        : String(payload.args.name);
                    const result = !!ui.hasAccess(id, name);
                    reply(event.source, event.origin, MessageType.UiResponse, requestId, { result });
                }
                catch (error) {
                    if (onError)
                        onError(error, { requestId, payload, action: 'ui.hasAccess' });
                    reply(event.source, event.origin, MessageType.UiError, requestId, {
                        message: String(error?.message || error),
                        code: 'ui_error',
                    });
                }
                return;
            }

            reply(event.source, event.origin, MessageType.UiError, requestId, {
                message: `Unsupported UI method: ${method}`,
                code: 'unsupported_method',
            });
            return;
        }

        if (type === MessageType.EventsRequest) {
            if (payload?.method !== 'runEventActions') {
                reply(event.source, event.origin, MessageType.EventsError, requestId, {
                    message: `Unsupported events method: ${payload?.method}`,
                    code: 'unsupported_method',
                });
                return;
            }
            if (typeof events?.runEventActions !== 'function') {
                reply(event.source, event.origin, MessageType.EventsError, requestId, {
                    message: 'runEventActions not available',
                    code: 'not_supported',
                });
                return;
            }
            const name = payload?.args?.name;
            if (!name || typeof name !== 'string') {
                reply(event.source, event.origin, MessageType.EventsError, requestId, {
                    message: 'name is required',
                    code: 'invalid_payload',
                });
                return;
            }
            try {
                const data = payload?.args?.data === undefined
                    ? null
                    : toPlainJson(payload.args.data);
                events.runEventActions(name, data);
                reply(event.source, event.origin, MessageType.EventsResponse, requestId, { result: null });
            }
            catch (error) {
                if (onError)
                    onError(error, { requestId, payload, action: 'events.runEventActions' });
                reply(event.source, event.origin, MessageType.EventsError, requestId, {
                    message: String(error?.message || error),
                    code: 'events_error',
                });
            }
            return;
        }

        if (type !== MessageType.ApiRequest)
            return;

        try {
            const method = payload?.method;
            const url = payload?.url;
            const data = payload?.data;

            if (!SUPPORTED_METHODS.has(method)) {
                reply(event.source, event.origin, MessageType.ApiError, requestId, {
                    message: `Unsupported method: ${method}`,
                    code: 'unsupported_method',
                });
                return;
            }

            const validation = validateApiUrl(url, getAllowedApiPrefixes(), { allowAbsoluteUrls });
            if (!validation.ok) {
                reply(event.source, event.origin, MessageType.ApiError, requestId, {
                    message: validation.reason,
                    code: 'url_not_allowed',
                });
                return;
            }

            const result = await executeApi(method, validation.url, data);
            reply(event.source, event.origin, MessageType.ApiResponse, requestId, { result });
        }
        catch (error) {
            if (onError)
                onError(error, { requestId, payload });

            const status = error?.StatusCode ?? error?.statusCode ?? error?.status;
            // Prefer a short text body (e.g. CLR "No Fonts…") over opaque Response stringification.
            let detail = error?.Result ?? error?.data ?? undefined;
            if (typeof Response !== 'undefined' && error instanceof Response) {
                try {
                    const bodyText = (await error.clone().text()).trim();
                    if (bodyText && bodyText.length <= 2000 && !/^</.test(bodyText))
                        detail = bodyText;
                }
                catch {
                    /* body already consumed or not text */
                }
            }
            reply(event.source, event.origin, MessageType.ApiError, requestId, {
                message: (typeof detail === 'string' && detail.trim())
                    ? detail.trim()
                    : describeApiError(error, status),
                code: status === 401 ? 'unauthorized' : 'api_error',
                status,
                // Never forward tokens or raw headers; include a shallow server payload when present.
                detail,
            });
        }
    }

    /**
     * Push updated params / route to the connected guest (after handshake).
     * @param {Record<string, unknown>} [params] Defaults to getContextParams()
     * @param {string} [route] Defaults to getContextRoute()
     */
    function pushContextUpdate(params, route) {
        if (disposed || !isGuestSourceCurrent())
            return;
        reply(guestSource, guestOrigin, MessageType.ContextUpdate, undefined, {
            params: resolveParams(params),
            route: resolveRoute(route),
        });
    }

    /**
     * Push updated theme colors to the connected guest (after handshake).
     * @param {{ colors?: Record<string, string>, name?: string } | null} [theme] Defaults to getTheme()
     */
    function pushThemeUpdate(theme) {
        if (disposed || !isGuestSourceCurrent())
            return;
        const snapshot = resolveTheme(theme);
        if (!snapshot)
            return;
        reply(guestSource, guestOrigin, MessageType.ThemeUpdate, undefined, snapshot);
    }

    return {
        handleMessage,
        pushContextUpdate,
        pushThemeUpdate,
        pushRealtimeStatus,
        dispose() {
            disposed = true;
            const ids = [...subscriptions.keys()];
            for (const id of ids)
                closeSubscription(id);
            closeAllPushSubscriptions();
            guestSource = null;
            guestOrigin = null;
        },
    };
}

/**
 * Portal API rejections are plain objects shaped { statusCode, reasonPhrase }
 * with no Error.message, so stringifying them yields "[object Object]".
 * A raw fetch Response stringifies to "[object Response]" — use status instead.
 * @param {unknown} error
 * @param {number} [status]
 * @returns {string}
 */
function describeApiError(error, status) {
    const reason =
        error?.message ||
        error?.Message ||
        error?.reasonPhrase ||
        error?.ReasonPhrase ||
        error?.statusText ||
        error?.error?.reasonPhrase ||
        error?.error?.ReasonPhrase;

    if (reason) {
        const text = String(reason).trim();
        if (text && text !== '[object Object]' && text !== '[object Response]')
            return text;
    }

    const text = String(error ?? '').trim();
    if (text && text !== '[object Object]' && text !== '[object Response]')
        return text;

    return status ? `Request failed with status ${status}` : 'Request failed';
}

export default createHostBridgeHandler;
